import React, { useRef, useEffect, useLayoutEffect, useMemo } from 'react';

interface DateScrollerProps {
  days: Array<{ dayId: string; dayDate: string; title?: string; color?: string }>;
  selectedDayId: string | null;
  onDateSelect: (dayId: string) => void;
  noCurve?: boolean; // If true, removes the curved top and negative margin
  scrollLeft?: number; // External scroll position to sync
  onScrollChange?: (scrollLeft: number) => void; // Callback when scroll position changes
  vertical?: boolean; // If true, displays dates vertically (for sidebar)
  useDayColors?: boolean; // If true, uses day colors instead of default styling
  transparent?: boolean; // If true, makes the scroller partially transparent
}

const DateScroller: React.FC<DateScrollerProps> = ({ 
  days, 
  selectedDayId, 
  onDateSelect, 
  noCurve = false,
  scrollLeft: externalScrollLeft,
  onScrollChange,
  vertical = false,
  useDayColors = false,
  transparent = false
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false);
  const lastExternalScrollRef = useRef<number | undefined>(undefined);
  const isScrollingToSelectedRef = useRef(false);
  const hasInitiallyScrolledRef = useRef(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return { day, month };
  };

  // Sort days by date
  const sortedDays = useMemo(() => {
    return [...days].sort((a, b) => 
      new Date(a.dayDate).getTime() - new Date(b.dayDate).getTime()
    );
  }, [days]);

  // Initial sync on mount - use useLayoutEffect to prevent flash
  useLayoutEffect(() => {
    if (scrollContainerRef.current && externalScrollLeft !== undefined) {
      scrollContainerRef.current.scrollLeft = externalScrollLeft;
      lastExternalScrollRef.current = externalScrollLeft;
    }
  }, []); // Only run on mount

  // Sync scroll position from external prop changes
  useEffect(() => {
    if (scrollContainerRef.current && externalScrollLeft !== undefined) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      // Only sync if the external value is different and we're not currently syncing
      if (Math.abs(currentScroll - externalScrollLeft) > 1 && !isSyncingRef.current) {
        isSyncingRef.current = true;
        scrollContainerRef.current.scrollLeft = externalScrollLeft;
        lastExternalScrollRef.current = externalScrollLeft;
        // Reset flag after sync completes
        requestAnimationFrame(() => {
          isSyncingRef.current = false;
        });
      }
    }
  }, [externalScrollLeft]);

  // Helper function to detect mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (typeof window !== 'undefined' && window.innerWidth <= 768);
  };

  // Scroll selected day to center when selectedDayId changes
  useEffect(() => {
    if (!selectedDayId || !scrollContainerRef.current || isScrollingToSelectedRef.current) {
      return;
    }

    // Skip if we're currently syncing with external scroll
    if (isSyncingRef.current) {
      return;
    }

    const scrollToSelected = () => {
      const container = scrollContainerRef.current;
      if (!container) return false;

      // Find the selected day button
      const selectedButton = container.querySelector(`[data-day-id="${selectedDayId}"]`) as HTMLElement;
      if (!selectedButton) return false;

      // Calculate scroll position to center the button
      const containerRect = container.getBoundingClientRect();
      const buttonRect = selectedButton.getBoundingClientRect();
      
      // Check if container has valid dimensions
      if (containerRect.width === 0 || containerRect.height === 0) {
        return false;
      }

      const containerCenter = containerRect.left + containerRect.width / 2;
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      const scrollOffset = buttonCenter - containerCenter;

      // Only scroll if the button is not already centered (with some tolerance)
      if (Math.abs(scrollOffset) > 5) {
        isScrollingToSelectedRef.current = true;
        const newScrollLeft = container.scrollLeft + scrollOffset;
        
        // On mobile, use instant scroll for initial load, smooth for subsequent changes
        const isInitialLoad = !hasInitiallyScrolledRef.current;
        const useSmooth = !isMobileDevice() || !isInitialLoad;
        
        container.scrollTo({
          left: newScrollLeft,
          behavior: useSmooth ? 'smooth' : 'auto'
        });

        // Update external scroll state if callback exists
        if (onScrollChange) {
          const updateScroll = () => {
            if (container) {
              const finalScrollLeft = container.scrollLeft;
              onScrollChange(finalScrollLeft);
              // Update lastExternalScrollRef to prevent external sync from overriding
              lastExternalScrollRef.current = finalScrollLeft;
            }
            hasInitiallyScrolledRef.current = true;
            isScrollingToSelectedRef.current = false;
          };

          if (useSmooth) {
            // Use setTimeout to update after scroll animation
            setTimeout(updateScroll, 300);
          } else {
            // For instant scroll, update immediately after a short delay
            setTimeout(updateScroll, 50);
          }
        } else {
          hasInitiallyScrolledRef.current = true;
          isScrollingToSelectedRef.current = false;
        }
        return true;
      } else {
        // Button is already centered, reset flag
        hasInitiallyScrolledRef.current = true;
        isScrollingToSelectedRef.current = false;
        return true;
      }
    };

    // On mobile, use multiple requestAnimationFrame calls to ensure layout is complete
    const isMobile = isMobileDevice();
    const attempts = isMobile ? 3 : 1;
    let attemptCount = 0;

    const tryScroll = () => {
      attemptCount++;
      const success = scrollToSelected();
      
      if (!success && attemptCount < attempts) {
        // Try again on next frame if not successful
        requestAnimationFrame(tryScroll);
      } else if (!success && isMobile) {
        // On mobile, also try with a small delay as fallback
        setTimeout(() => {
          scrollToSelected();
        }, 100);
      }
    };

    // Start with requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (isMobile) {
        // On mobile, wait an additional frame for layout calculations
        requestAnimationFrame(() => {
          // On mobile initial load, add a small delay to ensure layout is complete
          if (!hasInitiallyScrolledRef.current) {
            setTimeout(() => {
              tryScroll();
            }, 50);
          } else {
            tryScroll();
          }
        });
      } else {
        tryScroll();
      }
    });
  }, [selectedDayId, days.length, onScrollChange]);

  // Handle scroll events to notify parent
  const handleScroll = () => {
    if (scrollContainerRef.current && onScrollChange && !isSyncingRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      // Only update if it's a user-initiated scroll (not from external sync)
      if (lastExternalScrollRef.current === undefined || 
          Math.abs(currentScroll - lastExternalScrollRef.current) > 1) {
        onScrollChange(currentScroll);
      }
    }
  };

  // Helper to get button style for a day
  const getButtonStyle = (day: { dayId: string; dayDate: string; title?: string; color?: string }) => {
    const isSelected = selectedDayId === day.dayId;
    const dayColor = day.color || '#3B82F6';
    
    if (useDayColors) {
      // Use day color with transparency
      const opacity = isSelected ? 0.9 : 0.6; // Selected day less transparent
      const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(dayColor);
      if (rgb) {
        const r = parseInt(rgb[1], 16);
        const g = parseInt(rgb[2], 16);
        const b = parseInt(rgb[3], 16);
        return {
          backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})`,
          color: isSelected ? 'white' : '#0B1412',
          border: isSelected 
            ? `3px solid ${dayColor}` // Thicker, more prominent border for selected
            : `1px solid rgba(${r}, ${g}, ${b}, 0.3)`,
          boxShadow: isSelected ? `0 0 0 2px rgba(${r}, ${g}, ${b}, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15)` : 'none'
        };
      }
    }
    
    return {};
  };

  if (vertical) {
    // Vertical layout for sidebar
    return (
      <div 
        className={transparent ? '' : 'bg-[#F2FBF9]'}
        style={{
          position: 'relative',
          zIndex: 10,
          opacity: transparent ? 0.85 : 1.0,
          backgroundColor: transparent ? 'transparent' : undefined
        }}
      >
        <div 
          ref={scrollContainerRef}
          className="flex flex-col gap-2.5 overflow-y-auto scrollbar-hide" 
          style={{ 
            padding: '8px',
            maxHeight: '100%'
          }}
          onScroll={handleScroll}
        >
          {sortedDays.map((day) => {
            const { day: dayNum, month } = formatDate(day.dayDate);
            const isSelected = selectedDayId === day.dayId;
            
            return (
              <button
                key={day.dayId}
                data-day-id={day.dayId}
                onClick={() => onDateSelect(day.dayId)}
                className={`
                  flex-shrink-0 flex flex-col items-center justify-center
                  rounded-full transition-all duration-200
                  ${useDayColors ? '' : (isSelected 
                    ? 'bg-primary-500 text-white shadow-[0_2px_6px_rgba(0,0,0,0.04)]' 
                    : 'bg-white text-[#0B1412] border border-[#E3E8E5]'
                  )}
                `}
                style={{
                  width: '56px',
                  height: '56px',
                  ...getButtonStyle(day)
                }}
              >
                <span 
                  className="font-semibold"
                  style={{ fontSize: '14px', lineHeight: '1.2' }}
                >
                  {dayNum}
                </span>
                <span 
                  className="font-medium"
                  style={{ fontSize: '12px', lineHeight: '1.2', marginTop: '2px' }}
                >
                  {month}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Horizontal layout (default)
  return (
    <div 
      className={transparent ? '' : 'bg-[#F2FBF9]'}
      style={{
        ...(noCurve ? {} : {
          clipPath: 'ellipse(55% 50%)',
          marginTop: '-40px',
          paddingTop: '30px'
        }),
        position: 'relative',
        zIndex: 10,
        opacity: transparent ? 0.85 : 1.0,
        backgroundColor: transparent ? 'transparent' : undefined,
        ...(noCurve ? { padding: '12px' } : { padding: '16px 12px' })
      }}
    >
      <div 
        ref={scrollContainerRef}
        className="flex gap-2.5 overflow-x-auto scrollbar-hide" 
        style={{ paddingBottom: '4px' }}
        onScroll={handleScroll}
      >
        {sortedDays.map((day) => {
          const { day: dayNum, month } = formatDate(day.dayDate);
          const isSelected = selectedDayId === day.dayId;
          
          return (
            <button
              key={day.dayId}
              data-day-id={day.dayId}
              onClick={() => onDateSelect(day.dayId)}
              className={`
                flex-shrink-0 flex flex-col items-center justify-center
                rounded-full transition-all duration-200
                ${useDayColors ? '' : (isSelected 
                  ? 'bg-primary-500 text-white shadow-[0_2px_6px_rgba(0,0,0,0.04)]' 
                  : 'bg-white text-[#0B1412] border border-[#E3E8E5]'
                )}
              `}
              style={{
                width: '56px',
                height: '56px',
                ...getButtonStyle(day)
              }}
            >
              <span 
                className="font-semibold"
                style={{ fontSize: '14px', lineHeight: '1.2' }}
              >
                {dayNum}
              </span>
              <span 
                className="font-medium"
                style={{ fontSize: '12px', lineHeight: '1.2', marginTop: '2px' }}
              >
                {month}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DateScroller;

